import supabase from './supabaseClient.js';

// ---------------------------------------------------------------------------
// auth
// ---------------------------------------------------------------------------

export async function signUp(email, password, name) {
  const appUrl =
    import.meta.env.VITE_APP_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '');

  return supabase.auth.signUp({
    email,
    password,
    options: {
      data: { name },
      emailRedirectTo: `${appUrl}/`,
    },
  });
}

export async function signIn(email, password) {
  return supabase.auth.signInWithPassword({ email, password });
}

export async function signInWithMagicLink(email) {
  return supabase.auth.signInWithOtp({ email });
}

export async function signOut() {
  return supabase.auth.signOut();
}

export async function getCurrentUser() {
  return supabase.auth.getUser();
}

export async function resetPasswordForEmail(email) {
  const appUrl =
    import.meta.env.VITE_APP_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '');

  return supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${appUrl}/`,
  });
}

export async function updatePassword(newPassword) {
  return supabase.auth.updateUser({ password: newPassword });
}

// ---------------------------------------------------------------------------
// plans
// ---------------------------------------------------------------------------

export async function listPlans() {
  return supabase.from('plans').select('*');
}

// ---------------------------------------------------------------------------
// accounts
// ---------------------------------------------------------------------------

export async function getAccount(accountId) {
  return supabase.from('accounts').select('*').eq('id', accountId).single();
}

export async function updateAccountPlan(accountId, planId) {
  return supabase
    .from('accounts')
    .update({ plan_id: planId })
    .eq('id', accountId)
    .select()
    .single();
}

export async function createAccount(name, ownerId, planId) {
  return supabase
    .from('accounts')
    .insert({ name, owner_id: ownerId, plan_id: planId })
    .select()
    .single();
}

// ---------------------------------------------------------------------------
// profiles
// ---------------------------------------------------------------------------

export async function getProfile(userId) {
  return supabase.from('profiles').select('*').eq('id', userId).single();
}

export async function updateProfile(data) {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) return { data: null, error: userError };

  return supabase
    .from('profiles')
    .update(data)
    .eq('id', userData.user.id)
    .select()
    .single();
}

// ---------------------------------------------------------------------------
// accountUsers
// ---------------------------------------------------------------------------

export async function listMembers(accountId) {
  return supabase
    .from('account_users')
    .select('*')
    .eq('account_id', accountId)
    .order('created_at', { ascending: true });
}

export async function getUserAccountMembership(userId) {
  return supabase
    .from('account_users')
    .select('*, accounts(*)')
    .eq('user_id', userId)
    .eq('status', 'active')
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
}

export async function createAccountMember(accountId, userId, role, status = 'active') {
  return supabase
    .from('account_users')
    .insert({
      account_id: accountId,
      user_id: userId,
      role,
      status,
    })
    .select()
    .single();
}

export async function inviteMember(accountId, email, role) {
  return supabase
    .from('account_users')
    .insert({
      account_id: accountId,
      invited_email: email,
      role,
      status: 'pending',
    })
    .select()
    .single();
}

export async function removeMember(accountUserId) {
  return supabase.from('account_users').delete().eq('id', accountUserId);
}

export async function resendInvite(accountUserId) {
  const { data: member, error: fetchError } = await supabase
    .from('account_users')
    .select('invited_email')
    .eq('id', accountUserId)
    .single();

  if (fetchError) return { data: null, error: fetchError };

  return supabase.auth.signInWithOtp({ email: member.invited_email });
}

// ---------------------------------------------------------------------------
// brand storage
// ---------------------------------------------------------------------------

export const BRAND_ASSETS_BUCKET = 'brand-assets';

export function buildLogoStoragePath(accountId, brandId, ext) {
  const safeExt = ext.replace(/[^a-z0-9]/gi, '').toLowerCase() || 'png';
  return `brand-logos/${accountId}/${brandId}/logo.${safeExt}`;
}

export function buildAssetStoragePath(accountId, brandId, assetId, ext) {
  const safeExt = ext.replace(/[^a-z0-9]/gi, '').toLowerCase() || 'png';
  return `brand-assets/${accountId}/${brandId}/${assetId}.${safeExt}`;
}

export function getBrandAssetPublicUrl(storagePath) {
  const { data } = supabase.storage
    .from(BRAND_ASSETS_BUCKET)
    .getPublicUrl(storagePath);
  return data.publicUrl;
}

export async function uploadBrandFile(storagePath, file) {
  return supabase.storage
    .from(BRAND_ASSETS_BUCKET)
    .upload(storagePath, file, { upsert: true, contentType: file.type });
}

export async function deleteBrandFile(storagePath) {
  return supabase.storage.from(BRAND_ASSETS_BUCKET).remove([storagePath]);
}

function getFileExtension(file) {
  const fromName = file.name?.split('.').pop();
  if (fromName) {
    return fromName.toLowerCase();
  }

  const mime = file.type?.split('/').pop();
  return mime || 'png';
}

// ---------------------------------------------------------------------------
// brands
// ---------------------------------------------------------------------------

export async function listBrands(accountId) {
  return supabase
    .from('brands')
    .select('*')
    .eq('account_id', accountId)
    .order('created_at', { ascending: true });
}

export async function getBrand(brandId) {
  return supabase.from('brands').select('*').eq('id', brandId).single();
}

export async function createBrand(accountId, data) {
  return supabase
    .from('brands')
    .insert({ account_id: accountId, ...data })
    .select()
    .single();
}

export async function updateBrand(brandId, data) {
  return supabase
    .from('brands')
    .update(data)
    .eq('id', brandId)
    .select()
    .single();
}

export async function expandBusinessDescription({ brandId, businessSummary }) {
  const body = {
    action: 'expand_business_description',
    input: { businessSummary },
  };

  if (brandId) {
    body.brandId = brandId;
  }

  const { data, error } = await supabase.functions.invoke('brand-assistant', {
    body,
  });

  if (error) {
    return { data: null, error };
  }

  if (data?.error) {
    return {
      data: null,
      error: new Error(
        typeof data.error === 'string'
          ? data.error
          : 'Business description expansion failed.'
      ),
    };
  }

  return { data, error: null };
}

export async function enhancePostIdea({ brandId, seedContent }) {
  const body = {
    action: 'enhance_post_idea',
    input: { seedContent },
  };

  if (brandId) {
    body.brandId = brandId;
  }

  const { data, error } = await supabase.functions.invoke('brand-assistant', {
    body,
  });

  if (error) {
    return { data: null, error };
  }

  if (data?.error) {
    return {
      data: null,
      error: new Error(
        typeof data.error === 'string'
          ? data.error
          : 'Post idea enhancement failed.'
      ),
    };
  }

  return { data, error: null };
}

export async function draftVoiceAndCtas(brandId) {
  const { data, error } = await supabase.functions.invoke('brand-assistant', {
    body: {
      action: 'draft_voice_and_ctas',
      brandId,
    },
  });

  if (error) {
    return { data: null, error };
  }

  if (data?.error) {
    return {
      data: null,
      error: new Error(
        typeof data.error === 'string'
          ? data.error
          : 'Voice and CTA drafting failed.'
      ),
    };
  }

  return { data, error: null };
}

// ---------------------------------------------------------------------------
// brandConnections
// ---------------------------------------------------------------------------

export async function listConnections(brandId) {
  return supabase
    .from('brand_connections')
    .select('*')
    .eq('brand_id', brandId)
    .order('platform', { ascending: true });
}

export async function connectPlatform(brandId, platform, handle) {
  return supabase
    .from('brand_connections')
    .upsert(
      {
        brand_id: brandId,
        platform,
        handle,
        connected: true,
        connected_at: new Date().toISOString(),
      },
      { onConflict: 'brand_id,platform' }
    )
    .select()
    .single();
}

export async function disconnectPlatform(connectionId) {
  return supabase
    .from('brand_connections')
    .update({
      connected: false,
      handle: null,
      access_token: null,
      connected_at: null,
    })
    .eq('id', connectionId)
    .select()
    .single();
}

// ---------------------------------------------------------------------------
// brandAssets
// ---------------------------------------------------------------------------

export async function listAssets(brandId) {
  return supabase
    .from('brand_assets')
    .select('*')
    .eq('brand_id', brandId)
    .order('added_at', { ascending: false });
}

export async function addAsset(brandId, data) {
  return supabase
    .from('brand_assets')
    .insert({ brand_id: brandId, ...data })
    .select()
    .single();
}

export async function removeAsset(assetId) {
  const { data: asset, error: fetchError } = await supabase
    .from('brand_assets')
    .select('storage_path')
    .eq('id', assetId)
    .single();

  if (fetchError) {
    return { data: null, error: fetchError };
  }

  const { error: storageError } = await deleteBrandFile(asset.storage_path);

  if (storageError) {
    return { data: null, error: storageError };
  }

  return supabase.from('brand_assets').delete().eq('id', assetId);
}

export async function removeLogoAssets(brandId) {
  const { data: assets, error } = await listAssets(brandId);

  if (error) {
    return { error };
  }

  const logoAssets = (assets || []).filter((asset) => asset.source === 'logo');

  for (const asset of logoAssets) {
    const result = await removeAsset(asset.id);
    if (result.error) {
      return { error: result.error };
    }
  }

  return { error: null };
}

export async function uploadBrandLogo(accountId, brandId, file) {
  const storagePath = buildLogoStoragePath(
    accountId,
    brandId,
    getFileExtension(file)
  );
  const { error } = await uploadBrandFile(storagePath, file);

  if (error) {
    return { data: null, error };
  }

  return {
    data: {
      storagePath,
      publicUrl: getBrandAssetPublicUrl(storagePath),
    },
    error: null,
  };
}

export async function uploadBrandAssetFile(accountId, brandId, assetId, file) {
  const storagePath = buildAssetStoragePath(
    accountId,
    brandId,
    assetId,
    getFileExtension(file)
  );
  const { error } = await uploadBrandFile(storagePath, file);

  if (error) {
    return { data: null, error };
  }

  return {
    data: {
      storagePath,
      publicUrl: getBrandAssetPublicUrl(storagePath),
    },
    error: null,
  };
}

// ---------------------------------------------------------------------------
// posts
// ---------------------------------------------------------------------------

export function buildPostUploadStoragePath(accountId, brandId, postId, ext) {
  const safeExt = ext.replace(/[^a-z0-9]/gi, '').toLowerCase() || 'png';
  return `brand-assets/${accountId}/${brandId}/post-uploads/${postId}.${safeExt}`;
}

export function resolvePostImageUrl(imageStoragePath) {
  if (!imageStoragePath) {
    return '';
  }

  if (
    imageStoragePath.startsWith('http://')
    || imageStoragePath.startsWith('https://')
  ) {
    return imageStoragePath;
  }

  return getBrandAssetPublicUrl(imageStoragePath);
}

export function formatPostDate(iso) {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return 'Recently';
  }

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return `Today, ${date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })}`;
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function mapLocalPostToDb(draft) {
  const imagePath = draft.imageStoragePath
    || (draft.image && !draft.image.startsWith('blob:') ? draft.image : null);

  return {
    headline: draft.headline || '',
    subhead: draft.supporting || '',
    layout_id: draft.layout || 'editorial',
    ...(draft.prompt != null ? { prompt: draft.prompt } : {}),
    ...(imagePath ? { image_storage_path: imagePath } : {}),
    ...(draft.imageCredit ? { image_credit: draft.imageCredit } : {}),
  };
}

export async function savePostDraft(postId, draft) {
  return updatePost(postId, mapLocalPostToDb(draft));
}

export async function uploadPostImage(accountId, brandId, postId, file, label) {
  const uploadResult = await uploadPostScreenshot(
    accountId,
    brandId,
    postId,
    file
  );

  if (uploadResult.error) {
    return { data: null, error: uploadResult.error };
  }

  const { data: asset, error: assetError } = await addAsset(brandId, {
    storage_path: uploadResult.data.storagePath,
    label: label || file.name,
    source: 'post-upload',
  });

  if (assetError) {
    return { data: null, error: assetError };
  }

  const { data: post, error: postError } = await updatePost(postId, {
    image_storage_path: uploadResult.data.storagePath,
  });

  if (postError) {
    return { data: null, error: postError };
  }

  return {
    data: {
      post,
      asset,
      publicUrl: uploadResult.data.publicUrl,
      storagePath: uploadResult.data.storagePath,
    },
    error: null,
  };
}

export function mapDbPostToLocal(dbPost, captions = {}) {
  let caption = captions;

  if (Array.isArray(captions)) {
    caption = Object.fromEntries(
      captions.map((row) => [row.platform, row.caption])
    );
  }

  return {
    id: dbPost.id,
    brandId: dbPost.brand_id,
    prompt: dbPost.prompt || '',
    seedContent: dbPost.prompt || '',
    inputMode: dbPost.seed_type,
    headline: dbPost.headline || '',
    supporting: dbPost.subhead || '',
    image: resolvePostImageUrl(dbPost.image_storage_path),
    imageStoragePath: dbPost.image_storage_path,
    imageCredit: dbPost.image_credit || null,
    layout: dbPost.layout_id || 'editorial',
    status: dbPost.status,
    scheduledDate: dbPost.scheduled_date || null,
    postedDate: dbPost.posted_date
      ? String(dbPost.posted_date).slice(0, 10)
      : null,
    rejectionReason: dbPost.rejection_reason || '',
    publishedPlatforms: dbPost.published_platforms || [],
    createdAt: formatPostDate(dbPost.created_at),
    caption,
    engagement: null,
  };
}

export async function uploadPostScreenshot(accountId, brandId, postId, file) {
  const storagePath = buildPostUploadStoragePath(
    accountId,
    brandId,
    postId,
    getFileExtension(file)
  );
  const { error } = await uploadBrandFile(storagePath, file);

  if (error) {
    return { data: null, error };
  }

  return {
    data: {
      storagePath,
      publicUrl: getBrandAssetPublicUrl(storagePath),
    },
    error: null,
  };
}

export async function listPosts(brandId, filters = {}) {
  let query = supabase
    .from('posts')
    .select('*')
    .eq('brand_id', brandId)
    .order('created_at', { ascending: false });

  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  if (filters.fromDate) {
    query = query.gte('scheduled_date', filters.fromDate);
  }
  if (filters.toDate) {
    query = query.lte('scheduled_date', filters.toDate);
  }

  return query;
}

export async function getPost(postId) {
  return supabase.from('posts').select('*').eq('id', postId).single();
}

export async function createPost(brandId, data) {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) return { data: null, error: userError };

  const { id, ...fields } = data;

  return supabase
    .from('posts')
    .insert({
      brand_id: brandId,
      created_by: userData.user.id,
      ...fields,
      ...(id ? { id } : {}),
    })
    .select()
    .single();
}

export async function generatePost({
  brandId,
  seedType,
  seedContent,
  postId,
  mode = 'full',
}) {
  const body = { brandId, mode };

  if (mode === 'image_only') {
    body.postId = postId;
  } else {
    body.seedType = seedType;
    body.seedContent = seedContent;
    if (postId) {
      body.postId = postId;
    }
  }

  const { data, error } = await supabase.functions.invoke('generate-post', {
    body,
  });

  if (error) {
    return { data: null, error };
  }

  if (data?.error) {
    return {
      data: null,
      error: new Error(
        typeof data.error === 'string'
          ? data.error
          : 'Post generation failed.'
      ),
    };
  }

  return { data, error: null };
}

export async function generateCreativePost({
  brandId,
  mode,
  seedType,
  seedContent,
  genre,
  inputs,
  overrides,
}) {
  const body = { brandId, mode, seedType, seedContent };

  if (genre !== undefined) {
    body.genre = genre;
  }
  if (inputs !== undefined) {
    body.inputs = inputs;
  }
  if (overrides !== undefined) {
    body.overrides = overrides;
  }

  const { data, error } = await supabase.functions.invoke('generate-creative-post', {
    body,
  });

  if (error) {
    const message = data?.error
      ? (typeof data.error === 'string' ? data.error : JSON.stringify(data.error))
      : error.message || 'Creative post generation failed.';
    return { data: null, error: new Error(message) };
  }

  if (data?.error) {
    return {
      data: null,
      error: new Error(
        typeof data.error === 'string'
          ? data.error
          : 'Creative post generation failed.'
      ),
    };
  }

  return { data, error: null };
}

function invokeFunctionError(data, error, fallbackMessage) {
  if (error) {
    const message = data?.error
      ? (typeof data.error === 'string' ? data.error : JSON.stringify(data.error))
      : error.message || fallbackMessage;
    return { data: null, error: new Error(message) };
  }

  if (data?.error) {
    return {
      data: null,
      error: new Error(
        typeof data.error === 'string' ? data.error : fallbackMessage
      ),
    };
  }

  return null;
}

export async function guidedCreativeChat({ brandId, seedContent, messages }) {
  const body = { brandId };

  if (seedContent !== undefined) {
    body.seedContent = seedContent;
  }
  if (messages !== undefined) {
    body.messages = messages;
  }

  const { data, error } = await supabase.functions.invoke('guided-creative-chat', {
    body,
  });

  const failure = invokeFunctionError(
    data,
    error,
    'Guided creative chat failed.'
  );
  if (failure) {
    return failure;
  }

  return { data, error: null };
}

export async function generateGuidedCreative({ brandId, brief }) {
  const { data, error } = await supabase.functions.invoke(
    'generate-guided-creative',
    { body: { brandId, brief } }
  );

  const failure = invokeFunctionError(
    data,
    error,
    'Guided creative generation failed.'
  );
  if (failure) {
    return failure;
  }

  return { data, error: null };
}

export async function updatePost(postId, data) {
  return supabase
    .from('posts')
    .update(data)
    .eq('id', postId)
    .select()
    .single();
}

export async function approvePost(postId) {
  return supabase
    .from('posts')
    .update({ status: 'approved', rejection_reason: null })
    .eq('id', postId)
    .select()
    .single();
}

export async function rejectPost(postId, reason) {
  return supabase
    .from('posts')
    .update({ status: 'rejected', rejection_reason: reason })
    .eq('id', postId)
    .select()
    .single();
}

export async function schedulePost(postId, date) {
  return supabase
    .from('posts')
    .update({ scheduled_date: date })
    .eq('id', postId)
    .select()
    .single();
}

export async function publishPost(postId, platforms) {
  return supabase
    .from('posts')
    .update({
      status: 'posted',
      published_platforms: platforms,
      posted_date: new Date().toISOString(),
    })
    .eq('id', postId)
    .select()
    .single();
}

// ---------------------------------------------------------------------------
// postCaptions
// ---------------------------------------------------------------------------

export async function getCaptions(postId) {
  return supabase
    .from('post_captions')
    .select('*')
    .eq('post_id', postId)
    .order('platform', { ascending: true });
}

export async function upsertCaption(postId, platform, caption) {
  return supabase
    .from('post_captions')
    .upsert({ post_id: postId, platform, caption }, { onConflict: 'post_id,platform' })
    .select()
    .single();
}

// ---------------------------------------------------------------------------
// postEngagement
// ---------------------------------------------------------------------------

export function mapDbEngagementToLocal(row) {
  if (!row) {
    return null;
  }

  return {
    likes: row.likes ?? 0,
    comments: row.comments ?? 0,
    reach: row.reach ?? 0,
  };
}

export async function getEngagement(postId) {
  const { data, error } = await supabase
    .from('post_engagement')
    .select('*')
    .eq('post_id', postId)
    .maybeSingle();

  if (error) {
    return { data: null, error };
  }

  return { data: mapDbEngagementToLocal(data), error: null };
}

export async function getEngagementForPosts(postIds) {
  if (!postIds?.length) {
    return { data: {}, error: null };
  }

  const { data, error } = await supabase
    .from('post_engagement')
    .select('*')
    .in('post_id', postIds);

  if (error) {
    return { data: null, error };
  }

  const byPostId = Object.fromEntries(
    (data || []).map((row) => [row.post_id, mapDbEngagementToLocal(row)])
  );

  return { data: byPostId, error: null };
}

// ---------------------------------------------------------------------------
// namespaced exports
// ---------------------------------------------------------------------------

export const brands = {
  listBrands,
  getBrand,
  createBrand,
  updateBrand,
  expandBusinessDescription,
  enhancePostIdea,
  draftVoiceAndCtas,
};

export const brandConnections = {
  listConnections,
  connectPlatform,
  disconnectPlatform,
};

export const brandAssets = {
  listAssets,
  addAsset,
  removeAsset,
  removeLogoAssets,
  uploadBrandLogo,
  uploadBrandAssetFile,
  getBrandAssetPublicUrl,
  buildLogoStoragePath,
  buildAssetStoragePath,
};

export const posts = {
  listPosts,
  getPost,
  createPost,
  generatePost,
  generateCreativePost,
  guidedCreativeChat,
  generateGuidedCreative,
  updatePost,
  savePostDraft,
  approvePost,
  rejectPost,
  schedulePost,
  publishPost,
  mapDbPostToLocal,
  mapLocalPostToDb,
  resolvePostImageUrl,
  formatPostDate,
  uploadPostScreenshot,
  uploadPostImage,
  buildPostUploadStoragePath,
};

export const postCaptions = {
  getCaptions,
  upsertCaption,
};

export const postEngagement = {
  getEngagement,
  getEngagementForPosts,
  mapDbEngagementToLocal,
};
